import { ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import { type VariantProps, tv } from 'tailwind-variants';

const reveal = tv({
  base: 'transition-all',
  variants: {
    transition: {
      fade: 'opacity-0',
      slide: 'translate-y-10 opacity-0',
    },
    delay: {
      '0': 'delay-0',
      '100': 'delay-100',
      '200': 'delay-200',
      '300': 'delay-300',
      '500': 'delay-500',
    },
    duration: {
      '100': 'duration-100',
      '200': 'duration-200',
      '300': 'duration-300',
      '500': 'duration-500',
      '700': 'duration-700',
    },
    inView: {
      false: 'pointer-events-none',
      true: 'translate-y-0 opacity-100',
    },
  },
  defaultVariants: {
    transition: 'fade',
    delay: '0',
    duration: '500',
  },
});

export type Delay = VariantProps<typeof reveal>['delay'];

export default function Reveal({
  id,
  children,
  transition,
  delay,
  duration,
  threshold = 0.1,
  as = 'div',
  className,
  triggerOnce = true,
}: {
  id?: string;
  children: ReactNode;
  transition?: VariantProps<typeof reveal>['transition'];
  duration?: VariantProps<typeof reveal>['duration'];
  delay?: Delay;
  threshold?: number;
  as?: 'div' | 'section';
  className?: string;
  triggerOnce?: boolean;
}) {
  const Element = as;
  const { ref, inView } = useInView({
    threshold,
    triggerOnce,
  });

  return (
    <Element
      id={id}
      ref={ref}
      className={reveal({ transition, delay, duration, inView, className })}
    >
      {children}
    </Element>
  );
}
